import { addTokenToAuthorization, addTokenToHeaders, addCirxModuleToHeaders, Api } from "@/core/services/api.service";
import { CIRX_ANONYMOUS_TOKEN_EXPIRATION, CIRX_ANONYMOUS_USER } from "@/core/constants/api";
import Cookie from "@/assets/js/components/cookie";
import loginService from "@/core/services/login.service";
import { ORDservice } from "@/core/services/identification/organizationRootDetails.service";
import useUserStore from "@/apps/shared/user/store/userStore";
import { settleAll } from "@/core/utils/utils";
import userService from "@/core/services/api/user.service";
import organizationService from "@/core/services/api/organization.service";
import useAccountStore from "@/apps/shared/organization/store/accountStore";
import webtokenWithAccountAndOrdRepository from "@/apps/whiteLabelWebsite/organization/core/infrastructure/repository/webtokenWithAccountAndOrdRepository";
import {
  MODULE_LCM,
  MODULE_ROOT,
  MODULE_RPS,
  MODULE_TIM,
  NO_MODULE_TYPE,
  ROLE_ROOT_MANAGEMENT_LIST_PAGE,
  ROLE_USER_CAN_ASSIGN_ADMIN,
} from "../models/usersAndPermissions";
import useOrganizationRootDetailsStore from "@/core/store/organizationRootDetailsStore";

const identificationService = {
  /**
   * @return {User|null}
   */
  getUser() {
    const { currentUser } = useUserStore();

    if (!currentUser) {
      return null;
    }

    // force logout in case of outdated user
    const forceLogout = typeof currentUser === "object" && !Object.entries(currentUser.frontPermissions).length;
    if (forceLogout) {
      loginService.logout();
    } else {
      addTokenToHeaders(currentUser.apiToken);
      addCirxModuleToHeaders(currentUser.getCurrentModule());
    }
    return currentUser;
  },

  getToken() {
    const cirxUser = this.getUser();
    return cirxUser ? cirxUser.apiToken : null;
  },

  hasPermission(permission) {
    const cirxUser = this.getUser();
    return cirxUser?.frontPermissions[cirxUser.getCurrentModule() ?? NO_MODULE_TYPE]?.includes(permission);
  },

  isModuleType(moduleType) {
    return this.getUser()?.getCurrentModule() === moduleType;
  },

  isUserType(type) {
    return this.getUser()?.types?.includes(type) ?? false;
  },

  isUserSuperAdmin() {
    return this.hasPermission(ROLE_USER_CAN_ASSIGN_ADMIN);
  },

  isUserAdmin() {
    return this.hasPermission(ROLE_ROOT_MANAGEMENT_LIST_PAGE);
  },

  async askForPasswordReset(email) {
    const response = await Api.new().post("/password/renew", {
      email: email,
    });

    return response.data;
  },

  async checkPasswordToken(token) {
    const response = await Api.new().get(`/password/new/${token}`);

    return response.data;
  },

  async changeUserPassword(token, password) {
    const response = await Api.new().post(`/password/new/${token}`, { password: password });

    return response.data;
  },

  async getAnonymousUserWithORD(organizationURL) {
    const storedToken = Cookie.getCookie(CIRX_ANONYMOUS_USER);
    const storedORD = ORDservice.getOrganizationRootDetails();

    if (storedToken && storedORD && useAccountStore().isInitialized()) {
      addTokenToAuthorization(storedToken);
      return;
    }

    const { token, ord, account } =
      await webtokenWithAccountAndOrdRepository.getWebtokenWithAccountAndOrd(organizationURL);

    Cookie.setCookie(CIRX_ANONYMOUS_USER, token, {
      "max-age": CIRX_ANONYMOUS_TOKEN_EXPIRATION,
    });
    addTokenToAuthorization(token);
    useOrganizationRootDetailsStore().setFromOrdApi(ord);
    useAccountStore().initFromAccount(account);
  },

  removeAnonymousUser() {
    Cookie.deleteCookie(CIRX_ANONYMOUS_USER);
  },

  async fetchDashboardData() {
    const fileNameFormats = ["graphs_BO_data_{organizationId}.json"];
    const searchParams = new URLSearchParams();
    for (const fileNameFormat of fileNameFormats) {
      searchParams.append("fileNameFormats[]", fileNameFormat);
    }
    const queryString = searchParams.toString();

    const response = await Api.new().get(`/dashboard-data?${queryString}`);

    return response.data;
  },

  /**
   * Update the active module type of the user
   *
   * @param {string} moduleType
   */
  async switchCurrentOrganization(moduleType) {
    const { setUser, currentUser } = useUserStore();

    addCirxModuleToHeaders(moduleType);

    const [permissions, currentOrganization] = await settleAll([
      userService.fetchUserPermissions(currentUser.id),
      organizationService.getCurrentOrganization(currentUser.id),
    ]);

    currentUser.frontPermissions = permissions;
    currentUser.setCurrentOrganization(currentOrganization);

    setUser(currentUser);
  },

  /**
   * @param {string} moduleType
   * @return {string}
   */
  getModuleIconScr(moduleType) {
    switch (moduleType) {
      case MODULE_ROOT:
        return "/media/svg/icons/Home/Building.svg";
      case MODULE_TIM:
        return "/media/svg/icons/Shopping/Bag2.svg";
      case MODULE_LCM:
        return "/media/svg/icons/Communication/Incoming-box.svg";
      case MODULE_RPS:
        return "/media/svg/icons/General/Clipboard.svg";
      default:
        return "/media/svg/icons/Home/Building.svg";
    }
  },
};

export default identificationService;
